import React, {
  useState,
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import {
  Backdrop,
  Box,
  Modal,
  Fade,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSelector } from "react-redux";
import useStyles, { modalBoxStyle } from "./Styles";

interface Props {
  headerText: any;
  modalName?: string | ReactNode;
  children: ReactNode;
  styleClass?: any;
  modalCustomStyle?: any;
  showBottomBar?: boolean;
  handleSaveFunc?: () => void;
  buttonText?: string;
  // it won't close the modal upon saving, rather giving the control to the user,if they wish to close it upon success of the result while showing progress at the button and also not close the modal in case error gets encounted or process is still in progress
  modalNotCloseUponSave?: boolean;
  loading?: boolean;
  // disable the modal header button
  disableButton?: boolean;
  disableSaveButton?: boolean;
  dontCloseModalOnOutsideClick?: boolean;
  dontCloseModalOnCrossBtnClick?: boolean;
  variant?: "text" | "outlined" | "contained";
  tooltipText?: string; // Added optional tooltipText prop
  //
  setModalState?: any;
}

const ModalLayout: React.ForwardRefRenderFunction<any, Props> = (
  {
    headerText,
    modalName = "",
    children,
    modalCustomStyle,
    styleClass = undefined,
    showBottomBar = false,
    handleSaveFunc = () => {},
    buttonText = "Save changes",
    disableButton = false,
    modalNotCloseUponSave = false,
    loading = false,
    disableSaveButton = false,
    dontCloseModalOnOutsideClick = false,
    dontCloseModalOnCrossBtnClick = false,
    variant,
    tooltipText, // Destructure tooltipText prop
    //
    setModalState,
  },
  ref,
) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const { classes } = useStyles();
  const { connection }: any = useSelector((state: any) => state.connection);

  const handleSave = () => {
    if (!modalNotCloseUponSave) handleClose();
    handleSaveFunc?.();
  };

  useImperativeHandle(ref, () => ({
    handleOpen,
    handleClose,
    isOpen: open,
  }));

  useEffect(() => {
    setModalState?.(open);
  }, [open]);

  return (
    <Box>
      {modalName && (
        <Tooltip
          title={tooltipText || ""}
          placement="top"
          disableHoverListener={!tooltipText}
        >
          <span>
            {/* Use span to ensure tooltip works with disabled button */}
            <Button
              disableRipple
              variant={variant}
              disabled={disableButton}
              onClick={handleOpen}
              className={styleClass || classes.modalButton}
            >
              {modalName}
            </Button>
          </span>
        </Tooltip>
      )}
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={!dontCloseModalOnOutsideClick ? handleClose : () => {}}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={open}>
          <Box sx={{ ...modalBoxStyle, ...modalCustomStyle }}>
            <Box className={classes.headBox}>
              <Typography variant="body1" className={classes.headText}>
                {headerText}
              </Typography>
              <IconButton
                aria-label="Delete"
                className={classes.crossIconBtn}
                onClick={
                  !dontCloseModalOnCrossBtnClick ? handleClose : () => {}
                }
              >
                <Close className={classes.crossIconIcon} />
              </IconButton>
            </Box>
            <Box className={classes.centerBody}>{children}</Box>
            {showBottomBar && (
              <Box className={classes.bottomBtnBar}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.cancelBtn}
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  disabled={disableSaveButton || !connection}
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.saveButton}
                  onClick={handleSave}
                  endIcon={
                    loading && (
                      <CircularProgress size={15} sx={{ color: "white" }} />
                    )
                  }
                >
                  {buttonText}
                </Button>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default forwardRef(ModalLayout);
